import React, { useState } from 'react';
import { CheckboxRow, Modal, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { Colors } from 'library';
import { styled } from '@mui/material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { ShiftProviderUser } from 'models';
import { consoleLogInDev, useClient } from 'shared';

const INSTRUCTIONS = [
    "Download the substitute's complete compliance file from Tandem.",
    'Submit the file to the ELC for approval.',
    'Wait for confirmation that the substitute has been added to your application and is eligible to work in your VPK classroom(s).',
];

export default function VpkApplicationModal({
    provider_user,
    business_location_id,
    refresh,
    onClose,
}: {
    provider_user: ShiftProviderUser;
    business_location_id: number;
    refresh: () => void;
    onClose: () => void;
}) {
    const [confirmed, setConfirmed] = useState(false);
    const { loading, send } = useClient({
        url: 'api/staff/record-action/',
        options: { sendImmediately: false },
    });

    const fullName = `${provider_user.first_name} ${provider_user.last_name}`;

    function onComplete() {
        send({
            method: 'POST',
            body: {
                action: 'add_to_vpk_roster',
                provider_user_id: provider_user.id,
                business_location_id,
            },
        })
            .then(() => refresh())
            .catch((e) => {
                alert('An error occurred. Please try again. If this persists please reach out to support@join-tandem.com.');
                consoleLogInDev(e);
            })
            .finally(() => onClose());
    }

    return (
        <Modal onClose={onClose} width={560}>
            <Stack gap="lg">
                <Row gap="md" align="start">
                    <IconBadge>
                        <HowToRegIcon htmlColor={Colors.success} />
                    </IconBadge>
                    <Stack gap="sm">
                        <Title size="h2">Add to Your VPK Application</Title>
                        <Text tone="secondary" lineHeight="relaxed">
                            Follow the steps below to add {fullName} to your VPK application before their upcoming shift.
                            Confirm completion only after their file has been submitted for approval.
                        </Text>
                    </Stack>
                </Row>
                <Stack gap="md">
                    <Text size="xs" weight="bold" tone="secondary">
                        INSTRUCTIONS
                    </Text>
                    <Stack gap="md">
                        {INSTRUCTIONS.map((instruction, index) => (
                            <Row key={instruction} gap="sm" align="start">
                                <Text lineHeight="relaxed">{index + 1}.</Text>
                                <Text lineHeight="relaxed">{instruction}</Text>
                            </Row>
                        ))}
                    </Stack>
                </Stack>
                <CheckboxRow
                    checked={confirmed}
                    onChange={setConfirmed}
                    label={`I've submitted ${provider_user.first_name}'s file to the ELC for approval`}
                />
                <Row gap="sm" justify="end">
                    <PillButton variant="secondary" onClick={onClose}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={!confirmed || loading} onClick={onComplete}>
                        Mark Complete
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}

const IconBadge = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: Colors.successBackground,
});
